import { handleChange } from '@helpers/handleChange';
import { StoreState } from '@interfaces/stores';
import { formsService } from '@services/domain-factories/forms';
import { getCachedItemsByIds } from '@utils/getCachedItemsByIds';
import { createStoreWithSelector } from '@utils/zustand/createStoreWithSelector';
import { createUseStore } from '@utils/zustand/createUseStore';
import { initOnce } from '@utils/zustand/initOnce';
import { Form } from '@vincerosoft/assessfluence-types';

type Actions = {
  initForms: () => Promise<void>;
  handleChange: <T extends boolean>(
    change: T extends true ? string : Form,
    isDelete?: T extends true ? T : never
  ) => void;
  getForm: (id: string) => Promise<Form | null>;
  getFormsByIds: (ids: string[]) => Promise<Form[]>;
  reset: () => void;
};

const initialState: StoreState['forms'] & { resetToken: number } = {
  forms: null,
  loading: false,
  inFlightInit: null,
  isInitialized: false,
  // Bumped by reset() so in-flight async writes from a prior session can detect staleness and no-op.
  resetToken: 0
};

export const globalFormsStore = createStoreWithSelector<
  StoreState['forms'] & { resetToken: number } & Actions
>()((set, get) => ({
  ...initialState,
  handleChange: (change, isDelete) => {
    set((state) => ({
      forms: handleChange(state.forms || [], change, isDelete)
    }));
  },

  initForms: () =>
    initOnce<Form[]>(
      'isInitialized',
      set,
      get,
      () => formsService.getForms('formsStore', (forms) => set({ forms })),
      (forms) =>
        set({
          forms,
          isInitialized: true
        })
    ),

  getForm: async (id) => {
    const forms = get().forms || [];
    const inList = forms.find((f) => f.id === id);
    if (inList) {
      return inList;
    }

    return formsService.getForm('formsStore', id);
  },

  getFormsByIds: async (ids) => {
    const forms = get().forms || [];

    const resolvedItems = await getCachedItemsByIds<Form>({
      ids,
      cachedItems: forms,
      fetchByIds: (missingIds) => formsService.getFormsByIds('formsStore', missingIds)
    });

    return resolvedItems;
  },
  reset: () => set((state) => ({ ...initialState, resetToken: state.resetToken + 1 }))
}));

export const useGlobalFormsStore = createUseStore(globalFormsStore);
export const globalFormsStoreActions = (): Actions => globalFormsStore.getState();
